import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import React, { memo } from 'react';
import { Map } from 'immutable';
import classNames from 'classnames';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';

import uniqueId from '../../../utils/uniqueId';
import Table, { Column, Row } from '../../helpers/Table';
import { IconButton } from '../../helpers/IconButton';
import { noop } from '../../../utils/noop';
import { useEntityEventContext } from '../../../contexts/EntityEventContext';

import style from './style.module.css';

const noWrap = v => v;

const ContextMenuWrapper = (onClick, extraWrap = noWrap) => {
  const ContextMenuWrapped = (value, data) => {
    const id = uniqueId();
    const text = value === 'no date' ? null : value;

    return (
      <div>
        <ContextMenuTrigger id={id}>{extraWrap(text, data)}</ContextMenuTrigger>

        <ContextMenu id={id}>
          <MenuItem data={data.toJS()} onClick={onClick}>
            <i className="fa fa-remove" /> Delete
          </MenuItem>
        </ContextMenu>
      </div>
    );
  };

  return ContextMenuWrapped;
};

const wrapBacklogged = (text, data, backloggedActivitiesList) => {
  const activity = data.getIn(['activityMap', 'text'], '');

  if (data.get('backlogged', false) && !data.get('completed', false) && backloggedActivitiesList.includes(activity))
    return (
      <span>
        <i className="fa fa-clock-o" /> {text}
      </span>
    );

  return <span>{text}</span>;
};

/**
 * Stateless component for contact's activities.
 * Display table where rows are activities.
 */
const ContactActivitiesComponent = props => {
  const { onMouseEnter = noop, onMouseLeave = noop, backloggedActivities } = props;
  const { onAddEvent, onEditEvent, onDeleteEvent } = useEntityEventContext();

  /**
   * Var for activity rows.
   *
   * @type {React.Component[]}
   */
  const rows = props.events
    .get('all')
    .map(data => (
      <Row
        key={data.get('id')}
        className={getRowClassName(data)}
        data={data}
        onDoubleClick={event => onEditEvent(event, data)}
        onMouseEnter={event => onMouseEnter(event, data.get('id'))}
        onMouseLeave={event => onMouseLeave(event, data.get('id'))}
      />
    ));

  const dateTitle = (
    <>
      <IconButton className="mr5" icon="plus" onClick={onAddEvent} size="sm" title="Add activity" variant="primary" />
      <span>Date</span>
    </>
  );

  const current = props.events.get('current');
  const tooltipPosition = {
    top: current.get('tooltipTop'),
    left: current.get('tooltipLeft'),
  };

  const container = (
    <div className={style.tooltip} style={tooltipPosition}>
      {current.get('tooltipText')}
    </div>
  );
  const portal = createPortal(container, document.querySelector('body'));
  const tooltipPortal = current.get('showTooltip') && portal;

  return (
    <div className="contactActivities flexWrapper flexItem">
      <Table
        bodyClassName="flexItem"
        className={classNames(style.dates, 'table table-striped table-hover flexItem flexWrapper')}
      >
        <Column field="dateString" title={dateTitle} valueWrapper={ContextMenuWrapper(onDeleteEvent)} />
        <Column field="userName" title="Name" valueWrapper={ContextMenuWrapper(onDeleteEvent)} />
        <Column
          field="fullActivity"
          title="Activity"
          valueWrapper={ContextMenuWrapper(onDeleteEvent, (text, data) =>
            wrapBacklogged(text, data, backloggedActivities),
          )}
        />
        {rows}
      </Table>
      {tooltipPortal}
    </div>
  );
};

ContactActivitiesComponent.propTypes = {
  events: PropTypes.instanceOf(Map).isRequired,
};

export const ContactActivities = memo(ContactActivitiesComponent);

function getRowClassName(event) {
  const level = event.get('warnLevel');

  return classNames('clickable', {
    info: level === 0 || level === 1,
    warning: level === 2,
    danger: level === 3,
    'text-bold': event.get('isMarked'),
  });
}
